import {
    Animation,
    AnimationComponent,
    CollisionEventType,
    Component,
    MeshComponent,
    PhysXComponent,
    WonderlandEngine,
} from '@wonderlandengine/api';
import {property} from '@wonderlandengine/api/decorators.js';
import {Enemy, EnemyStates} from '../state/Enemy.js';
import {vec3} from 'gl-matrix';
import {AxisY} from '../utils/math.js';
import {PlayerComponent} from './player-component.js';
import {PlayerStates} from '../state/Player.js';
import {AudioSource} from '@wonderlandengine/spatial-audio';
import {gameState} from '../state/GameState.js';

const tempVec = vec3.create();
const tempVec2 = vec3.create();

export const allEnemies: EnemyComponent[] = new Array();
/**
 * Get all enemies withing a certain sphere.
 *
 * @note This looks inefficient, but should never take more than 0.1ms
 *
 * @param center Center point
 * @param radius Radius of the search sphere
 */
export function enemiesWithinRange(
    center: vec3,
    radius: number,
    allowDead = false
): EnemyComponent[] {
    const radiusSq = radius * radius;
    const res = [];
    for (const e of allEnemies) {
        if (e.isDestroyed || (!allowDead && e.getData().dead)) continue;
        e.object.getPositionWorld(tempVec);

        if (vec3.squaredDistance(center, tempVec) < radiusSq) {
            res.push(e);
        }
    }
    return res;
}

/**
 * Component to represent an enemy in the game
 *
 * This component is responsible for:
 * - Aggroing the enemy when the player is within aggro distance
 * - Attacking the player when the player is within attack reach
 * - Taking damage from the player
 * - Dying when health reaches 0
 *
 * An enemy aggros when a player is within distance and the enemy is not aggroed yet.
 * In case the enemy is aggroed on 1 player it ignores other players.
 * When an enemy is hit by a player, it aggroes on that player.
 * If the aggroed player dies, is lost unless another player is in the aggro distance.
 */
export class EnemyComponent extends Component {
    static TypeName = 'enemy-component';

    static onRegister(engine: WonderlandEngine) {
        engine.registerComponent(AudioSource);
    }

    @property.int(1)
    level = 1;

    @property.float(2)
    movementSpeed = 2;

    @property.float(2)
    attackReach = 2;

    @property.float(4)
    aggroDistance = 4;

    @property.float(25)
    followDistance = 25;

    @property.animation()
    animIdle: Animation | null = null;
    @property.animation()
    animRun: Animation | null = null;
    @property.animation()
    animHurt: Animation | null = null;
    @property.animation()
    animSpawn: Animation | null = null;
    @property.animation()
    animAttack: Animation | null = null;
    @property.animation()
    animDeath: Animation | null = null;

    private _enemyData!: Enemy;
    private _attackReachPhysX!: PhysXComponent;
    private _aggroAreaPhysX!: PhysXComponent;
    private _aggroOnPlayer: PlayerComponent | null = null;
    private _animation: AnimationComponent | null = null;
    private _moving = false;
    private _audioComponent: AudioSource | null = null;

    start() {
        if (this.object.isDestroyed) {
            throw new Error('enemy-component: start() called on destroyed object');
        }

        this._enemyData = new Enemy(this.object.name, this.level);
        this._enemyData.propertyChanged.add(this._onEnemyPropertyChanged);

        this._enemyData.canHitPlayer = () => {
            // as long as we're in single player this works. With more players we need to
            //check if the aggroed player is within attack range
            return this._playersWithinAttack.length > 0;
        };

        // Add a PhysXComponent to see if the player is within attack reach
        this._attackReachPhysX = this.object.addComponent(PhysXComponent, {
            extents: [this.attackReach, 0, 0],
            kinematic: true,
            trigger: true,
            groupsMask: 0b10000000,
            blocksMask: 0b10000000,
        })!;

        // Add a PhysXComponent to see aggro when player is within aggro distance
        this._aggroAreaPhysX = this.object.addComponent(PhysXComponent, {
            extents: [this.aggroDistance, 0, 0],
            kinematic: true,
            trigger: true,
            groupsMask: 0b10000000,
            blocksMask: 0b10000000,
        })!;

        this._aggroAreaPhysX.onCollision(this._onAggroAreaCollision);
        this._attackReachPhysX.onCollision(this._onAttackReachCollision);

        this._animation = this.object.getComponent(AnimationComponent);
        const skin =
            this.object.children[1].getComponent(MeshComponent)?.skin ??
            this.object.children[0].getComponent(MeshComponent)?.skin;
        if (skin) {
            this.animIdle = this.animIdle?.retarget(skin) ?? null;
            this.animRun = this.animRun?.retarget(skin) ?? null;
            this.animHurt = this.animHurt?.retarget(skin) ?? null;
            this.animSpawn = this.animSpawn?.retarget(skin) ?? null;
            this.animAttack = this.animAttack?.retarget(skin) ?? null;
            this.animDeath = this.animDeath?.retarget(skin) ?? null;
            this._animation?.onEvent.add((event) => {
                console.log(event);
            });
        }

        if (this._animation) {
            this._animation.active = true;
            this._enemyData.setState(EnemyStates.Spawn);
        }

        if (this._enemyData.name == 'Skeleton Golem') {
            this._audioComponent = this.object.addComponent(AudioSource, {
                src: 'audio/sfx/golem_walk_large.mp3',
                loop: true,
                spatial: true,
                volume: 6,
            });
        }
        allEnemies.push(this);
    }

    onDestroy(): void {
        const i = allEnemies.indexOf(this);
        allEnemies.splice(i, 1);
    }

    update(dt: number) {
        if (this._enemyData.state === EnemyStates.Dead) {
            return;
        }

        let moving = false;

        if (this._aggroOnPlayer) {
            // calculate distance to player
            this.object.getPositionWorld(tempVec2);
            this._aggroOnPlayer.object.getPositionWorld(tempVec);

            if (
                vec3.squaredDistance(tempVec, tempVec2) >
                    this.followDistance * this.followDistance ||
                gameState.currentPlayer.state == PlayerStates.Dying ||
                gameState.currentPlayer.state == PlayerStates.Dead
            ) {
                this._aggroOnPlayer = null;
                moving = false;
                this._enemyData.clearAggro();
                this._enemyData.setState(EnemyStates.Idle);
                return;
            }

            tempVec[1] = tempVec2[1];
            this.object.lookAt(tempVec);
            this.object.rotateAxisAngleDegObject(AxisY, 180);

            // if player is outside attack range, move towards player
            if (this._playersWithinAttack.indexOf(this._aggroOnPlayer) === -1) {
                this.object.getForwardWorld(tempVec);
                vec3.scale(tempVec, tempVec, -dt * this.movementSpeed);

                // todo: Cast a ray to see if there's something in the way
                this.object.translateWorld(tempVec);

                this._enemyData.setState(EnemyStates.Move);
                moving = true;
            }
        } else {
            moving = false;
        }
        if (this._moving != moving) {
            if (moving) {
                this._enemyData.setState(EnemyStates.Move);
            } else {
                //are there any players within range? then attack
                this._enemyData.setState(EnemyStates.Idle);
            }
        }
        this._moving = moving;
    }

    setAggroOnPlayer(player: PlayerComponent) {
        if (
            gameState.currentPlayer.state === PlayerStates.Dead ||
            gameState.currentPlayer.state === PlayerStates.Dying
        ) {
            return;
        }
        this._aggroOnPlayer = player;
        this._enemyData.aggro(player.getData());
    }

    private _playersWithinRange: PlayerComponent[] = [];
    private _playersWithinAttack: PlayerComponent[] = [];

    private _onAggroAreaCollision = (type: CollisionEventType, other: PhysXComponent) => {
        if (other.object.isDestroyed) return;

        if (type === CollisionEventType.TriggerTouch) {
            const player = other.object.getComponent(PlayerComponent);
            if (player) {
                this._playersWithinRange.push(player);
                if (!this._aggroOnPlayer) {
                    this._aggroOnPlayer = player;
                    this._enemyData.aggro(player.getData());
                }
            }
        }
        if (type === CollisionEventType.TriggerTouchLost) {
            const player = other.object.getComponent(PlayerComponent);
            if (player) {
                const index = this._playersWithinRange.indexOf(player);
                if (index > -1) {
                    this._playersWithinRange.splice(index, 1);
                }
            }
        }
    };

    _onAttackReachCollision = (type: CollisionEventType, other: PhysXComponent) => {
        if (other.object.isDestroyed) return;
        if (type === CollisionEventType.TriggerTouch) {
            const player = other.object.getComponent(PlayerComponent);
            if (player) {
                this._playersWithinAttack.push(player);
            }
        }
        if (type === CollisionEventType.TriggerTouchLost) {
            const player = other.object.getComponent(PlayerComponent);
            if (player) {
                const index = this._playersWithinAttack.indexOf(player);
                if (index > -1) {
                    this._playersWithinAttack.splice(index, 1);
                }
            }
        }
    };

    getData() {
        return this._enemyData;
    }

    private _onEnemyPropertyChanged = (property: string) => {
        if (property === 'dead' && this._enemyData.dead) {
            this.object.destroy();
        }
        if (property === 'state') {
            switch (this._enemyData.state) {
                case EnemyStates.Spawn:
                    this._animation!.animation = this.animSpawn;
                    this._animation!.playCount = 1;
                    break;
                case EnemyStates.Idle:
                    this._animation!.animation = this.animIdle;
                    this._animation!.playCount = 0;
                    if (this._audioComponent) this._audioComponent.stop();
                    break;
                case EnemyStates.Move:
                    this._animation!.animation = this.animRun;
                    this._animation!.playCount = 0;
                    if (this._audioComponent && !this._audioComponent.isPlaying) {
                        this._audioComponent.play();
                    }
                    break;
                case EnemyStates.Hit:
                    this._animation!.animation = this.animHurt;
                    this._animation!.playCount = 0;
                    break;
                case EnemyStates.Attack:
                    this._animation!.animation = this.animAttack;
                    this._animation!.playCount = 0;
                    if (this._audioComponent && !this._audioComponent.isPlaying) {
                        this._audioComponent.play();
                    }
                    break;
                case EnemyStates.Dead:
                    this._animation!.animation = this.animDeath;
                    this._animation!.playCount = 1;
                    if (this._audioComponent) this._audioComponent.stop();
                    break;
            }
            this._animation!.play();
        }
    };
}
